import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* -------------------------------------------------------------------------
 * RedemptionsPage — full "Recent Redemptions" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const columns = [
  { key: "code", label: "Code", width: "1.2fr" },
  { key: "name", label: "Customer Name", width: "1.4fr" },
  { key: "email", label: "Email", width: "1.5fr" },
  { key: "value", label: "Value", width: "0.8fr", render: (r) => `$${r.value.toFixed(2)}` },
  { key: "status", label: "Status", width: "0.8fr" },
  { key: "date", label: "Redeem at", width: "1.2fr", align: "right" },
];

const RedemptionsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Redemptions | Omaha Food Lovers";
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("redeemed_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // Filter states

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("page_size", 20);
    params.append("sort_by", sortBy);
    params.append("sort_direction", sortDirection);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    return params.toString();
  };

  const fetchRedemptions = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.restaurantAllRedemptions}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const rawRedemptions = data.redemptions || data.data || [];

        // Transform API data to component format
        const newRedemptions = rawRedemptions.map(redemption => ({
          id: redemption.id,
          code: redemption.code || 'N/A',
          name: redemption.customer?.name || `${redemption.customer?.first_name || ''} ${redemption.customer?.last_name || ''}`.trim() || 'Unknown',
          email: redemption.customer?.email || 'N/A',
          value: redemption.value || redemption.value_cents / 100 || 0,
          status: redemption.status_label || redemption.status || 'Unknown',
          date: redemption.redeemed_at_display || redemption.redeemed_at || 'Unknown',
        }));

        if (isLoadMore) {
          setRedemptions((prev) => [...prev, ...newRedemptions]);
        } else {
          setRedemptions(newRedemptions);
        }

        setHasMore(newRedemptions.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch redemptions:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchRedemptions(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.restaurantExport}?type=redemptions`, {
        responseType: 'blob',
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'redemptions_export.csv');
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toastr.success('Redemptions exported successfully');
    } catch (error) {
      console.error('Failed to export redemptions:', error);
      toastr.error('Failed to export redemptions');
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "redeemed_at",
      options: [
        { value: "redeemed_at", label: "Redeemed At" },
        { value: "code", label: "Code" },
        { value: "value", label: "Value" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchRedemptions(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Recent Redemptions</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of recent voucher redemptions and customer activity.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by Code"
            filters={filters}
            showExport
            onExport={handleExport}
          />

        <DataTable
          columns={columns}
          rows={redemptions}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        /></div>
      </Container>
    </div>
  );
};

RedemptionsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

RedemptionsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default RedemptionsPage;
